import React, { useEffect, useRef, useState } from "react";
import axios, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

// Handles the Emergent OAuth redirect: reads `#session_id=…` from the URL,
// exchanges it via the backend for a persistent session_token cookie,
// then clears the hash and refreshes the auth state so the app rerenders
// as signed-in.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
export const AuthCallback = () => {
  const [status, setStatus] = useState("Signing you in…");
  const hasProcessed = useRef(false);
  const { refresh } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    (async () => {
      if (!sessionId) {
        setStatus("Missing session id — redirecting home…");
        window.history.replaceState({}, "", "/");
        window.location.replace("/");
        return;
      }
      try {
        await axios.post(`${API_BASE}/auth/session`, null, {
          headers: { "X-Session-ID": sessionId },
        });
        await refresh();
        toast.success("Signed in with Google");
      } catch {
        toast.error("Google sign-in failed. Please try again.");
      } finally {
        window.history.replaceState({}, "", "/");
        window.location.replace("/");
      }
    })();
  }, [refresh]);

  return (
    <div
      data-testid="auth-callback-screen"
      className="min-h-screen w-full flex items-center justify-center bg-slate-50"
    >
      <div className="flex flex-col items-center gap-6">
        <Logo variant="mark" height={80} data-testid="callback-brand-mark" />
        <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
        <p className="text-sm text-slate-600" style={{ fontFamily: "Manrope, sans-serif" }}>
          {status}
        </p>
      </div>
    </div>
  );
};
